import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import { orderConfirmedQueryOptions } from '@lib/query/options/orders';
import PageStatusMessage from '@modules/order/components/page-status-message';
import OrderCompletedTemplate from '@modules/order/templates/order-completed-template';
import { SkeletonOrderConfirmed } from '@pages/order/skeleton';

/**
 * Order Confirmed Page
 *
 * 結帳完成後的訂單確認頁面。
 * 使用聚合 BFF API (GET /api/orders/:id/confirmed) 取得含 enriched items 和 marxPayment 的訂單資料。
 *
 * 使用 useQuery 而非 useSuspenseQuery，搭配 enabled guard 避免 id 為空時觸發查詢。
 */
export default function OrderConfirmedPage() {
  const { id } = useParams<{ countryCode: string; id: string }>();

  const { data, isPending, isError, refetch } = useQuery({
    ...orderConfirmedQueryOptions(id ?? ''),
    enabled: !!id,
  });

  if (isPending) {
    return <SkeletonOrderConfirmed />;
  }

  if (isError) {
    return (
      <PageStatusMessage
        data-testid="order-confirmed-error"
        title="無法載入訂單資料"
        description="請稍後再試"
        onRetry={() => refetch()}
      />
    );
  }

  if (!data?.order) {
    return (
      <PageStatusMessage
        data-testid="order-confirmed-not-found"
        title="找不到訂單資料"
        description="訂單資料不存在或已被移除"
        onRetry={() => refetch()}
      />
    );
  }

  return <OrderCompletedTemplate order={data.order} />;
}
